import { useState, useEffect } from 'react';
import type { Task } from '@cowork-gen/core';
import { wsClient } from './api/client';
import { TaskList } from './components/TaskList';
import { LogViewer } from './components/LogViewer';
import { StatsCard } from './components/StatsCard';
import { Settings } from './components/Settings';
import { CreateTaskModal } from './components/CreateTaskModal';

type Tab = 'dashboard' | 'settings';

export default function App() {
  const [activeTab, setActiveTab] = useState<Tab>('dashboard');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    // Connect to WebSocket for real-time updates
    wsClient.connect();

    return () => {
      wsClient.disconnect();
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold text-gray-900">Cowork-Gen</h1>
              <nav className="flex gap-2">
                <button
                  onClick={() => setActiveTab('dashboard')}
                  className={`px-3 py-1 rounded-md text-sm font-medium ${
                    activeTab === 'dashboard'
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Dashboard
                </button>
                <button
                  onClick={() => setActiveTab('settings')}
                  className={`px-3 py-1 rounded-md text-sm font-medium ${
                    activeTab === 'settings'
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Settings
                </button>
              </nav>
            </div>
            {activeTab === 'dashboard' && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
              >
                Create Task
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {activeTab === 'dashboard' ? (
          <div className="space-y-6">
            {/* Stats */}
            <StatsCard />

            {/* Task List and Log Viewer */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <TaskList
                onSelectTask={setSelectedTask}
                selectedTaskId={selectedTask?.id}
              />
              <div className="h-[600px]">
                <LogViewer task={selectedTask} />
              </div>
            </div>
          </div>
        ) : (
          <Settings />
        )}
      </main>

      {/* Create Task Modal */}
      <CreateTaskModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={() => {
          setShowCreateModal(false);
        }}
      />
    </div>
  );
}
